import { useEffect, useState } from "react";
import { refreshFirebaseDownloadUrl } from "@/shared/lib/firebaseStorage";

type FirebaseImageProps = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  "src"
> & {
  src: string | undefined;
};

/**
 * <img> that self-heals stale Firebase Storage download URLs.
 *
 * Firebase download URLs embed a token that 403s once the object is
 * re-uploaded or its token revoked. This renders the stored URL as-is (no
 * up-front network cost), and only when the image fails to load does it
 * re-mint a fresh token via getDownloadURL — so a gallery of hundreds of
 * already-valid photos pays nothing, while the broken ones recover. Re-mint is
 * attempted once per src; non-Firebase URLs simply error as a normal <img>.
 */
export function FirebaseImage({ src, onError, ...rest }: FirebaseImageProps) {
  const [resolvedSrc, setResolvedSrc] = useState(src);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    setResolvedSrc(src);
    setTried(false);
  }, [src]);

  const handleError: React.ReactEventHandler<HTMLImageElement> = async (e) => {
    if (!tried && resolvedSrc) {
      setTried(true);
      const fresh = await refreshFirebaseDownloadUrl(resolvedSrc);
      if (fresh && fresh !== resolvedSrc) {
        setResolvedSrc(fresh);
        return;
      }
    }
    onError?.(e);
  };

  return <img src={resolvedSrc} onError={handleError} {...rest} />;
}
